import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, Shield, KeyRound, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import AuthFooter from '../components/AuthFooter';

type Mode = 'password' | 'otp-email' | 'otp-code';

export default function Login() {
  const { user, signInWithPassword, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const prefilledEmail = searchParams.get('email')?.trim().toLowerCase() ?? '';

  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      const target = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state]);

  const validEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!validEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setLoading(true);
    try {
      await signInWithPassword(trimmed, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const trimmed = email.trim().toLowerCase();
    if (!validEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await requestOtp(trimmed);
      setEmail(trimmed);
      setInfo(`We sent a 6-digit code to ${trimmed}. It expires in 10 minutes.`);
      setMode('otp-code');
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const cleaned = code.replace(/\D/g, '').slice(0, 6);
    if (cleaned.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(email, cleaned);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await requestOtp(email);
      setInfo(`We sent a fresh code to ${email}.`);
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B2341] via-[#1A3A5C] to-[#0B2341] grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-3 mb-4">
            <img
              src="https://adiimmigration.com.au/wp-content/uploads/2021/06/ADI_Immigration-white.png"
              alt="ADI Immigration"
              className="w-24 h-24"
            />
            <div className="text-slate-300 text-sm">Client Portal</div>
          </div>
          <h1 className="text-white text-2xl font-semibold mb-1">Welcome back</h1>
          <p className="text-slate-300 text-sm">Sign in to access your visa application</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div>
                <label className="form-label">Email address</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="form-input pl-10"
                    required
                    autoFocus={!prefilledEmail}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="form-label !mb-0">Password</label>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/forgot-password${
                          email.trim() ? `?email=${encodeURIComponent(email.trim().toLowerCase())}` : ''
                        }`,
                      )
                    }
                    className="text-xs text-[#2CABEA] hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className="form-input pl-10 pr-10"
                    required
                    autoFocus={!!prefilledEmail}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && <ErrorBanner message={error} />}
              {info && <InfoBanner message={info} />}

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Signing in...' : <>Sign in <ArrowRight size={16} /></>}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setMode('otp-email');
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Email me a 6-digit code instead
                </button>
              </div>
            </form>
          )}

          {mode === 'otp-email' && (
            <form onSubmit={handleRequestCode} className="space-y-5">
              <div>
                <label className="form-label">Email address</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="form-input pl-10"
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>
                <p className="form-hint">We'll send you a 6-digit code to sign in without a password.</p>
              </div>

              {error && <ErrorBanner message={error} />}

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Sending code...' : <>Email me a code <ArrowRight size={16} /></>}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setMode('password');
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                >
                  <ArrowLeft size={12} /> Back to password sign-in
                </button>
              </div>
            </form>
          )}

          {mode === 'otp-code' && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div>
                <label className="form-label">6-digit code</label>
                <div className="relative">
                  <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="form-input pl-10 tracking-[0.5em] text-center font-semibold text-lg"
                    required
                    autoFocus
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
                {info && <p className="form-hint text-slate-600">{info}</p>}
              </div>

              {error && <ErrorBanner message={error} />}

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Verifying...' : <>Sign in <ArrowRight size={16} /></>}
              </button>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setMode('otp-email');
                    setCode('');
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                >
                  <ArrowLeft size={12} /> Use a different email
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-[#2CABEA] hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <Shield size={12} />
              Secured sign-in. We never share your data.
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-slate-400 text-xs">
          <p>
            Need help? Call{' '}
            <a href="tel:1300729331" className="text-[#2CABEA] hover:underline">
              1300 729 331
            </a>{' '}
            or email{' '}
            <a href="mailto:adi@adibusinessgroup.com" className="text-[#2CABEA] hover:underline">
              adi@adibusinessgroup.com
            </a>
          </p>
        </div>

        <AuthFooter />
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  );
}

function InfoBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
      {message}
    </div>
  );
}

function humaniseError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? 'Something went wrong.');
  const lower = raw.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid_credentials')) {
    return 'Email or password is incorrect. Try again, use "Forgot password", or sign in with a code.';
  }
  if (lower.includes('rate') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (lower.includes('expired')) {
    return 'That code has expired. Tap "Resend code" to get a new one.';
  }
  if (lower.includes('invalid') && lower.includes('token')) {
    return 'That code is incorrect. Double-check the email or tap "Resend code".';
  }
  if (lower.includes('signups not allowed') || lower.includes('user not found')) {
    return 'We don\u2019t have an account for that email. Check the address, or contact your case manager.';
  }
  if (lower.includes('invalid') && lower.includes('email')) {
    return 'That email address looks off. Please check and try again.';
  }
  return raw;
}
